import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CONTENT_TYPES, STATUS_FILTER } from "./content-types";
import { effectiveQuery, facetsQuery, searchQuery } from "./search-params";

// LF360-2030: the search widget's state and its requests.
//
// Every change of keyword, filter, date or sort starts over from the first
// page and asks for the counts again; answers to anything asked before the
// latest change are dropped, so a slow first keystroke cannot overwrite what
// the last one found.

const TYPING_DELAY_MS = 250;

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

const EMPTY_FACETS = { total: null, counts: {}, updated: [] };
const NO_ITEMS = [];

export const useContentSearch = ({ types, open, defaultType, browse = false, access }) => {
  const typesKey = types.join(",");
  const stableTypes = useMemo(() => typesKey.split(","), [typesKey]);
  const [typeKey, setTypeKey] = useState(defaultType && types.includes(defaultType) ? defaultType : types[0]);
  const type = CONTENT_TYPES[typeKey];
  const filterDefs = useMemo(
    () => (browse && type.supportsStatus !== false ? [STATUS_FILTER, ...type.filters] : type.filters),
    [browse, type]
  );

  useEffect(() => {
    if (open) {setTypeKey(defaultType && stableTypes.includes(defaultType) ? defaultType : stableTypes[0]);}
  }, [open, defaultType, stableTypes]);

  const [query, setQuery] = useState("");
  const [filtersByType, setFiltersByType] = useState(() =>
    Object.fromEntries(types.map((key) => [key, {}]))
  );
  const [date, setDate] = useState(null);
  const [sort, setSort] = useState(null);

  // Results are kept with the type they were found for. A tab switch changes
  // the type at once and the results only when the new search answers; until
  // then the old ones are not this type's to show (All reads a row's own type
  // from it, which a single type's rows do not carry).
  const [found, setFound] = useState({ typeKey: null, items: NO_ITEMS });
  const items = found.typeKey === typeKey ? found.items : NO_ITEMS;
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [facets, setFacets] = useState(EMPTY_FACETS);
  const [optionsByType, setOptionsByType] = useState({});

  const filters = filtersByType[typeKey] || {};
  const debouncedQuery = useDebounced(query, TYPING_DELAY_MS);
  // What is actually sent: typing a first or second letter changes nothing the
  // backend is asked, so it starts no request.
  const sentQuery = effectiveQuery(debouncedQuery);
  const generation = useRef(0);

  const state = useMemo(
    () => ({ query: sentQuery, filters, date, sort }),
    [sentQuery, filters, date, sort]
  );

  // What each filter can be set to: once per type, while the widget is open.
  useEffect(() => {
    if (!open || optionsByType[typeKey]) {return;}

    let cancelled = false;
    type
      .filterOptions()
      .then(({ data }) => {
        if (cancelled) {return;}
        setOptionsByType((prev) => ({
          ...prev,
          [typeKey]: Object.fromEntries(filterDefs.map((f) => [f.key, f.optionsOf(data || {})])),
        }));
      })
      .catch((e) => console.error("Content search: filter options failed", e));

    return () => {
      cancelled = true;
    };
  }, [open, typeKey, type, filterDefs, optionsByType]);

  // The first page and the counts, whenever what is asked changes.
  useEffect(() => {
    if (!open) {return;}

    const mine = ++generation.current;
    setLoading(true);
    setError(null);

    const search = (params) => (["all", "settings"].includes(typeKey) ? type.search(params, stableTypes, access) : type.search(params));
    const facets = (params) => (["all", "settings"].includes(typeKey) ? type.facets(params, stableTypes, access) : type.facets(params));
    Promise.all([search(searchQuery(state, filterDefs)), facets(facetsQuery(state, filterDefs))])
      .then(([page, counted]) => {
        if (mine !== generation.current) {return;}
        setFound({ typeKey, items: page.data?.items || NO_ITEMS });
        setHasMore(Boolean(page.data?.hasMore));
        setCursor(page.data?.nextCursor || null);
        setFacets({ ...EMPTY_FACETS, ...counted.data });
      })
      .catch((e) => {
        if (mine !== generation.current) {return;}
        console.error("Content search failed", e);
        setFound({ typeKey, items: NO_ITEMS });
        setHasMore(false);
        setFacets(EMPTY_FACETS);
        setError(e);
      })
      .finally(() => {
        if (mine === generation.current) {setLoading(false);}
      });
    return () => {generation.current += 1;};
  }, [open, type, typeKey, state, filterDefs, stableTypes, access]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading || !cursor) {return;}

    const mine = generation.current;
    setLoading(true);

    type
      .search(searchQuery({ ...state, cursor }, filterDefs), ...(typeKey === "all" ? [stableTypes, access] : []))
      .then(({ data }) => {
        if (mine !== generation.current) {return;}
        setFound((prev) => {
          if (prev.typeKey !== typeKey) {return prev;}
          const seen = new Set(prev.items.map((item) => `${item.contentType || typeKey}:${item.id}`));

          return {
            typeKey,
            items: [...prev.items, ...(data?.items || []).filter((item) => !seen.has(`${item.contentType || typeKey}:${item.id}`))],
          };
        });
        setHasMore(Boolean(data?.hasMore));
        setCursor(data?.nextCursor || null);
      })
      .catch((e) => {
        if (mine !== generation.current) {return;}
        console.error("Content search: next page failed", e);
        setHasMore(false);
      })
      .finally(() => {
        if (mine === generation.current) {setLoading(false);}
      });
  }, [hasMore, loading, cursor, type, typeKey, state, filterDefs, stableTypes, access]);

  const setFilters = useCallback(
    (update) =>
      setFiltersByType((prev) => ({
        ...prev,
        [typeKey]: typeof update === "function" ? update(prev[typeKey]) : update,
      })),
    [typeKey]
  );

  const toggleFilterValue = useCallback(
    (filterKey, value) =>
      setFilters((current) => {
        const values = current[filterKey] || [];

        return {
          ...current,
          [filterKey]: values.includes(value) ? values.filter((v) => v !== value) : [...values, value],
        };
      }),
    [setFilters]
  );

  /** Every filter and the date gone. */
  const clearAll = useCallback(() => {
    setFilters({});
    setDate(null);
  }, [setFilters]);

  return {
    type,
    typeKey,
    setTypeKey,
    query,
    setQuery,
    debouncedQuery,
    filters,
    toggleFilterValue,
    setFilters,
    date,
    setDate,
    sort,
    setSort,
    clearAll,
    items,
    hasMore,
    loading,
    error,
    loadMore,
    facets,
    options: optionsByType[typeKey] || {},
    filterDefs,
  };
};
